import useAccordion from "@/hooks/useAccordion";
import { forwardRef, MutableRefObject, useRef } from "react";

import styles from "./Card.module.css";

import Button from "../forms/Button";
import IconArrow, { Direction } from "../icons/IconArrow";


export interface CardProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  children?: React.ReactNode;
  active?: boolean;
}

export default function Card({children, active = false, className, ...rest}: CardProps) {
  return (
    <div className={`${styles.card} ${active ? styles.active : ""} ${className ?? ""}`} {...rest}>
      {children}
    </div>
  );
}

// top border is ignored for the first child
// bottom border is ignored for the last child
// top border overrides the bottom border of the previous sibling
export interface CardContentProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  children?: React.ReactNode;
  borderBottom?: "solid" | "dashed" | "none";
  borderTop?: "solid" | "dashed" | "none";
}

export const CardContent = forwardRef(function CardContent({
  children,
  borderBottom = "dashed",
  borderTop = "none",
  className,
  ...rest
}: CardContentProps, ref: MutableRefObject<HTMLDivElement>) {
  const borderBottomClass = {
    solid: styles.borderBottomSolid,
    dashed: styles.borderBottomDashed,
    none: "",
  }[borderBottom];
  const borderTopClass = {
    solid: styles.borderTopSolid,
    dashed: styles.borderTopDashed,
    none: "",
  }[borderTop];

  return (
    <div ref={ref} className={`${styles.contentWrapper} ${borderBottomClass} ${borderTopClass}`}>
      <div className={styles.contentPadding}>
        <div className={`${styles.content} ${className ?? ""}`} {...rest}>
          {children}
        </div>
      </div>
    </div>
  );
});

export interface CardAccordionProps {
  children?: React.ReactNode;
  collapsed?: boolean;
}

export function CardAccordion({children, collapsed: propsCollapsed}: CardAccordionProps) {
  const ref = useRef();
  const { collapsed, setCollapsed} = useAccordion(ref, propsCollapsed, "0px");
  if (propsCollapsed !== collapsed) {
    setCollapsed(propsCollapsed);
  }
  return <div className={styles.accordion} ref={ref}>{children}</div>
}

export interface CollapseButtonProps {
  collapsed?: boolean;
  onChange?: (collapsed: boolean) => void;
}

export function CollapseButton({ collapsed, onChange }: CollapseButtonProps) {
  return (
    <Button type="text" onClick={e => { e.stopPropagation(); onChange?.(!collapsed) }}>
      <IconArrow size={24} direction={collapsed ? Direction.Down : Direction.Up} style={{ transition: "transform 200ms" }} />
    </Button>
  );
}
